import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ABOUT, STATS } from '@/constants/testIds';

const STATS_DATA = [
    { id: 'years', value: 5, suffix: '+', label: 'Years of Excellence' },
    { id: 'events', value: 100, suffix: '+', label: 'Events Delivered' },
    { id: 'corporate', value: 100, suffix: '+', label: 'Happy Clients' },
    { id: 'cities', value: 7, suffix: '+', label: 'Cities Served' },
];

const Counter = ({ target, suffix, testId }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-80px' });
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const duration = 1800;
        const start = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * target));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => raf && cancelAnimationFrame(raf);
    }, [inView, target]);

    return (
        <div ref={ref} data-testid={testId} className="font-display font-light text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            <span className="tabular-nums">{value.toLocaleString()}</span>
            <span className="text-gold">{suffix}</span>
        </div>
    );
};

export const About = () => {
    return (
        <>
            <section id="about" data-testid={ABOUT.root} className="relative py-24 sm:py-32 bg-black overflow-hidden">
                <div className="container-lux">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
                        <div className="lg:col-span-4">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-8 h-px bg-gold" />
                                <span className="eyebrow">About Mindstorm</span>
                            </div>
                            <div className="text-white/40 text-[11px] tracking-[0.3em] uppercase">01 / Studio</div>
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="lg:col-span-8"
                        >
                            <h2 data-testid={ABOUT.title} className="font-display text-white font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-10">
                                Crafting Moments.
                                <br />
                                <span className="italic text-gold font-extralight">Creating Impact.</span>
                            </h2>
                            <p data-testid={ABOUT.body} className="text-white/70 text-lg leading-relaxed max-w-2xl font-light">
                                Every event is an opportunity to inspire, engage, and leave a lasting impression. Mindstorm Productions combines creativity, technology, and flawless execution to create extraordinary corporate experiences across India. From concept to curtain call, we bring every detail together.
                            </p>
                        </motion.div>
                    </div>

                    {/* Editorial two-image composition */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.9, delay: 0.1 }}
                            className="lg:col-span-8 relative overflow-hidden aspect-[16/10]"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85"
                                alt="Live event audience"
                                className="w-full h-full object-cover"
                                style={{ filter: 'brightness(0.85) contrast(1.05)' }}
                            />
                            <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.3em] uppercase text-white/80 bg-black/40 backdrop-blur-sm px-3 py-2">
                                Audience Engagement
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.9, delay: 0.25 }}
                            className="lg:col-span-4 relative overflow-hidden aspect-[16/10] lg:aspect-auto"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1596040078821-9b5f50e71ea5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBjb3Jwb3JhdGUlMjBldmVudCUyMHN0YWdlJTIwbGVkJTIwd2FsbCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85"
                                alt="Cinematic stage production"
                                className="w-full h-full object-cover"
                                style={{ filter: 'brightness(0.85) contrast(1.05)' }}
                            />
                            <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.3em] uppercase text-white/80 bg-black/40 backdrop-blur-sm px-3 py-2">
                                Stage Production
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section data-testid={STATS.root} className="relative py-20 sm:py-24 bg-neutral-950 border-y border-white/5">
                <div className="container-lux grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
                    {STATS_DATA.map((s, i) => (
                        <motion.div
                            key={s.id}
                            data-testid={STATS.item(s.id)}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="relative"
                        >
                            <div className="text-gold text-[10px] tracking-[0.3em] mb-3 opacity-70">0{i + 1}</div>
                            <Counter testId={STATS.value(s.id)} target={s.value} suffix={s.suffix} />
                            <div className="mt-4 text-white/60 text-xs sm:text-sm tracking-[0.2em] uppercase font-light">
                                {s.label}
                            </div>
                            <div className="mt-6 w-10 h-px bg-gold/60" />
                        </motion.div>
                    ))}
                </div>
            </section>
        </>
    );
};

export default About;
